import type { JSX } from 'react';
import { useEffect } from 'react';
import type { LossMode, SaveDoc } from '@bratva/state';
import { store } from '../game/store.js';

interface Props {
  save: SaveDoc;
}

const rub = (kop: number): string => `${(kop / 100).toLocaleString('ru-RU')} ₽`;

const TITLES: Record<'srok' | 'mogila', { title: string; verdict: string }> = {
  srok: {
    title: 'Дело закрыто',
    verdict: 'Суд. Пять лет строгого режима — и всё, что нажито, осталось во дворе.',
  },
  mogila: {
    title: 'Отбегал',
    verdict: 'Забили у гаражей. Двор помнит, но дальше двор живёт без тебя.',
  },
};

/**
 * Финальный экран: он же и разбор. Показывает не только «конец»,
 * но и где именно игрок зарвался, чтобы следующий заход был осмысленнее.
 */
export function GameOver({ save }: Props): JSX.Element | null {
  if (save.outcome === 'active') return null;
  const { title, verdict } = TITLES[save.outcome];

  // Закрытое дело уезжает в архив один раз, при показе финала.
  useEffect(() => {
    void store.archiveIfFinished();
  }, [save.outcome, save.seed]);

  const restart = (mode: LossMode): void => {
    void store.startNew(mode);
  };

  return (
    <div className="over">
      <div className="over-card">
        <span className="hud-caption">Дело №{store.currentSlot}</span>
        <h1>{title}</h1>
        <p className="over-verdict">{verdict}</p>

        <dl className="over-stats">
          <div>
            <dt>Дней</dt>
            <dd>{save.day}</dd>
          </div>
          <div>
            <dt>Уровень</dt>
            <dd>{save.player.level}</dd>
          </div>
          <div>
            <dt>Авторитет</dt>
            <dd>{save.player.avtoritet}</dd>
          </div>
          <div>
            <dt>В кармане</dt>
            <dd>{rub(save.player.cash)}</dd>
          </div>
          <div>
            <dt>Точек</dt>
            <dd>{save.businesses.length}</dd>
          </div>
          <div>
            <dt>Территорий</dt>
            <dd>{save.territories.length}</dd>
          </div>
        </dl>

        <p className="over-note">
          {save.justice.raids > 1
            ? `Облав было ${save.justice.raids}. Палево копилось быстрее, чем ты его сбрасывал: сбыт, отсидка и смотрящий — три способа, которыми это лечится.`
            : 'Дела шли ровно, а закончилось резко. Следующий раз держи здоровье выше сорока перед наездом.'}
        </p>

        <div className="over-actions">
          <button type="button" className="primary" onClick={() => restart(save.mode)}>
            Новое дело
          </button>
          <button type="button" onClick={() => restart(save.mode === 'pacanski' ? 'mamkin' : 'pacanski')}>
            {save.mode === 'pacanski' ? 'Начать по-мамкиному' : 'Начать по-пацански'}
          </button>
        </div>
      </div>
    </div>
  );
}
